import { useMemo, useState } from "react";

import {
  getMeasurementDistribution,
  matchAllPathways,
  parsePathwayDsl,
  type PathwayMatch,
  type PathwayPattern,
} from "@thalamos/pathway-logic";
import type { DetentionPeriod } from "@thalamos/pathway-logic";

import { MeasurementDistributionModal } from "./MeasurementDistributionModal.js";

type Props = {
  patientId: string;
  detentionPeriods: DetentionPeriod[];
  pathwayDsl: string | null;
  allPatientsMatches: PathwayMatch[]; // All matches across all patients
  totalPatientsMatched: number;
};

/**
 * Sidebar shown when viewing a single patient's full-screen graph.
 * Shows the pattern matched, and this patient's measurement values
 * highlighted on the distribution graphs.
 */
export const PatientPathwaySidebar = ({
  patientId,
  detentionPeriods,
  pathwayDsl,
  allPatientsMatches,
  totalPatientsMatched,
}: Props) => {
  const [expandedMeasure, setExpandedMeasure] = useState<string | null>(null);

  // Parse pattern
  const pattern = useMemo((): PathwayPattern | null => {
    if (!pathwayDsl) return null;
    try {
      return parsePathwayDsl(pathwayDsl);
    } catch {
      return null;
    }
  }, [pathwayDsl]);

  // Find matches for this patient
  const patientMatches = useMemo(() => {
    if (!pattern) return [];
    return matchAllPathways(detentionPeriods, pattern);
  }, [pattern, detentionPeriods]);

  // Get measurement names from pattern
  const measurementNames = useMemo(() => {
    const names = new Set<string>();
    pattern?.measurements?.forEach((m) => names.add(m.name));
    patientMatches.forEach((m) => {
      m.measurements.forEach((ms) => names.add(ms.name));
    });
    return Array.from(names);
  }, [pattern, patientMatches]);

  // Get this patient's measurements for each type
  const patientMeasurements = useMemo(() => {
    const result: Record<string, number[]> = {};
    patientMatches.forEach((match) => {
      match.measurements.forEach((m) => {
        if (m.days >= 0) {
          (result[m.name] ??= []).push(m.days);
        }
      });
    });
    return result;
  }, [patientMatches]);

  if (!pattern || !pathwayDsl) {
    return (
      <div style={{ marginTop: "2em", padding: 10, textAlign: "center" }}>
        <div style={{ fontSize: 13, color: "#6b7280" }}>
          No pathway filter active
        </div>
      </div>
    );
  }

  const isMatched = patientMatches.length > 0;

  return (
    <>
      <div
        style={{
          marginTop: "2em",
          padding: 12,
          background: isMatched ? "#ecfdf5" : "#fef3c7",
          borderRadius: 8,
          border: isMatched ? "2px solid #10b981" : "2px solid #f59e0b",
        }}
      >
        {/* Pattern info */}
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: 11,
              color: isMatched ? "#065f46" : "#92400e",
              marginBottom: 4,
            }}
          >
            Pattern
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: isMatched ? "#047857" : "#b45309",
              lineHeight: 1.3,
            }}
          >
            {pattern.name}
          </div>
        </div>

        {/* Match status */}
        <div
          style={{
            padding: 8,
            background: isMatched
              ? "rgba(16, 185, 129, 0.1)"
              : "rgba(245, 158, 11, 0.1)",
            borderRadius: 6,
            marginBottom: 12,
            textAlign: "center",
          }}
        >
          {isMatched ? (
            <>
              <div style={{ fontSize: 10, color: "#065f46" }}>
                This patient has
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#059669" }}>
                {patientMatches.length}
              </div>
              <div style={{ fontSize: 10, color: "#065f46" }}>
                matching pathway{patientMatches.length !== 1 ? "s" : ""}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#b45309" }}>
                ✗ No match
              </div>
              <div style={{ fontSize: 10, color: "#92400e" }}>
                Patient doesn&apos;t match this pattern
              </div>
            </>
          )}
        </div>

        {/* Measurements for this patient */}
        {isMatched && measurementNames.length > 0 && (
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 8,
              }}
            >
              Measurements
            </div>
            {measurementNames.map((name) => {
              const values = patientMeasurements[name] ?? [];
              const onlyValue = values.length === 1 ? values[0] : undefined;
              const dist = getMeasurementDistribution(allPatientsMatches, name);
              const measurementDef = pattern?.measurements?.find(
                (m) => m.name === name,
              );
              const measurementType = measurementDef?.type ?? "duration";
              const unit = measurementType === "count" ? "×" : "d";

              // Calculate percentile position for this patient's values
              const getPercentile = (v: number) => {
                if (dist.values.length === 0) return null;
                const below = dist.values.filter((x) => x < v).length;
                return Math.round((below / dist.values.length) * 100);
              };

              return (
                <div
                  key={name}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setExpandedMeasure(name);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setExpandedMeasure(name);
                    }
                  }}
                  style={{
                    marginBottom: 10,
                    padding: 10,
                    background: "white",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#4f46e5";
                    e.currentTarget.style.background = "#f5f3ff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e5e7eb";
                    e.currentTarget.style.background = "white";
                  }}
                  title="Click to see full distribution"
                >
                  <div
                    style={{ fontSize: 10, color: "#6b7280", marginBottom: 2 }}
                  >
                    {name}
                  </div>

                  {/* This patient's value(s) */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                    }}
                  >
                    <div>
                      <span
                        style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: "#dc2626",
                        }}
                      >
                        {onlyValue !== undefined
                          ? `${String(onlyValue)}${unit}`
                          : values.map((v) => `${String(v)}${unit}`).join(", ")}
                      </span>
                      {onlyValue !== undefined &&
                        getPercentile(onlyValue) !== null && (
                          <span
                            style={{
                              fontSize: 10,
                              color: "#6b7280",
                              marginLeft: 4,
                            }}
                          >
                            ({getPercentile(onlyValue)}th percentile)
                          </span>
                        )}
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 9, color: "#9ca3af" }}>
                        pop. median
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#4f46e5",
                        }}
                      >
                        {dist.median}
                        {unit}
                      </div>
                    </div>
                  </div>

                  {/* Mini distribution bar with patient marker */}
                  <div style={{ marginTop: 8 }}>
                    <svg
                      width="100%"
                      height={24}
                      style={{ overflow: "visible" }}
                    >
                      {/* Background bar */}
                      <rect
                        x="0%"
                        y={8}
                        width="100%"
                        height={8}
                        fill="#e5e7eb"
                        rx={4}
                      />
                      {/* IQR range */}
                      {dist.max > dist.min && (
                        <rect
                          x={`${String(((dist.p25 - dist.min) / (dist.max - dist.min)) * 100)}%`}
                          y={8}
                          width={`${String(((dist.p75 - dist.p25) / (dist.max - dist.min)) * 100)}%`}
                          height={8}
                          fill="#c7d2fe"
                          rx={2}
                        />
                      )}
                      {/* Median line */}
                      {dist.max > dist.min && (
                        <line
                          x1={`${String(((dist.median - dist.min) / (dist.max - dist.min)) * 100)}%`}
                          y1={6}
                          x2={`${String(((dist.median - dist.min) / (dist.max - dist.min)) * 100)}%`}
                          y2={18}
                          stroke="#4f46e5"
                          strokeWidth={2}
                        />
                      )}
                      {/* Patient value markers */}
                      {values.map((v, i) => {
                        const x =
                          dist.max === dist.min
                            ? 50
                            : ((v - dist.min) / (dist.max - dist.min)) * 100;
                        return (
                          <circle
                            key={i}
                            cx={`${String(x)}%`}
                            cy={12}
                            r={5}
                            fill="#dc2626"
                            stroke="white"
                            strokeWidth={2}
                          />
                        );
                      })}
                    </svg>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 8,
                        color: "#9ca3af",
                      }}
                    >
                      <span>
                        {dist.min}
                        {unit}
                      </span>
                      <span>
                        {dist.max}
                        {unit}
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      fontSize: 9,
                      color: "#9ca3af",
                      textAlign: "center",
                      marginTop: 4,
                    }}
                  >
                    click to expand →
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Population stats */}
        <div
          style={{
            marginTop: 12,
            padding: 8,
            background: "#f3f4f6",
            borderRadius: 4,
            fontSize: 10,
            color: "#6b7280",
            textAlign: "center",
          }}
        >
          {totalPatientsMatched} patients match this pattern
        </div>
      </div>

      {/* Expanded measurement modal */}
      {expandedMeasure && (
        <MeasurementDistributionModal
          name={expandedMeasure}
          matches={allPatientsMatches}
          numPatients={totalPatientsMatched}
          measurementType={
            pattern?.measurements?.find((m) => m.name === expandedMeasure)
              ?.type ?? "duration"
          }
          isOpen={true}
          onClose={() => {
            setExpandedMeasure(null);
          }}
          highlightedPatient={{
            patientId,
            measurementName: expandedMeasure,
            values: patientMeasurements[expandedMeasure] || [],
          }}
        />
      )}
    </>
  );
};

export default PatientPathwaySidebar;
